/*
 * Products the visitor opened, newest first, persisted per browser in
 * localStorage. A snapshot of the card fields is stored with each id so the
 * home rail and the search palette can render without a fetch.
 * Pushed by the product page only.
 */
import { defineStore } from 'pinia';
import type { ProductSummary } from '../types/product';

export const RECENT_MAX = 8;
const STORAGE_KEY = 'keyzo.recent.v1';

export type RecentProduct = Pick<
  ProductSummary,
  'id' | 'name' | 'platform' | 'ourPriceCents' | 'coverUrl' | 'imageUrl' | 'isPreorder' | 'totalQty' | 'offerCount' | 'available'
>;

function load(): RecentProduct[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(isRecent).slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function isRecent(x: unknown): x is RecentProduct {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.id === 'string' && typeof o.name === 'string' && typeof o.ourPriceCents === 'number';
}

function persist(items: RecentProduct[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // private mode / quota — the rail is a convenience, not state we need
  }
}

export const useRecentlyViewedStore = defineStore('recentlyViewed', {
  state: () => ({ items: load() }),
  actions: {
    push(p: RecentProduct) {
      const snapshot: RecentProduct = {
        id: p.id,
        name: p.name,
        platform: p.platform,
        ourPriceCents: p.ourPriceCents,
        coverUrl: p.coverUrl,
        imageUrl: p.imageUrl,
        isPreorder: p.isPreorder,
        totalQty: p.totalQty,
        offerCount: p.offerCount ?? null,
        available: p.available ?? true,
      };
      this.items = [snapshot, ...this.items.filter((i) => i.id !== p.id)].slice(0, RECENT_MAX);
      persist(this.items);
    },
    clear() {
      this.items = [];
      persist(this.items);
    },
  },
});
